import {
  Autocomplete,
  Box,
  Button,
  CloseButton,
  Flex,
  Grid,
  Image,
  PasswordInput,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import useStyles from "./style";
import LinePatternSvg from "../../components/Svgs/LinePatternSvg";
import { useForm } from "@mantine/form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GoChevronDown } from "react-icons/go";
import { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { getSignupAddressByPlaceId, SignupsearchAddress, skypointSignUp } from "../../services";


const contractorRoles = [
  "QDDP",
  "Billing Agency",
  "Accounting",
  "Documentation Specialist",
  "Home Health Agency",
  "Staffing Agency",
  "Consulting Firm",
  "Therapy Provider",
  "Others",
];

const extractAddressFields = (res) => {
  const components = res?.result?.address_components || [];
  const formattedAddress = res?.result?.formatted_address || "";

  const get = (type, short = false) =>
    components.find((c) => c.types.includes(type))?.[
      short ? "short_name" : "long_name"
    ] || "";

  const city =
    get("locality") || get("administrative_area_level_2") || "";
  const state = get("administrative_area_level_1", true) || "";
  const postal_code = get("postal_code") || "";
  const countryShort = get("country", true) || "";
  const countryFull = get("country", false) || "";

  let line1 = formattedAddress;
  [postal_code, state, city, countryFull].forEach((val) => {
    if (val)
      line1 = line1.replace(
        new RegExp(`\\s*,?\\s*${val}\\s*,?\\s*`, "g"),
        " "
      );
  });
  line1 = line1
    .replace(/\s*,\s*/g, ", ")
    .replace(/,+/g, ",")
    .replace(/^,|,$/, "")
    .trim()
    .replace(/\s+/g, " ");

  return {
    line1: res?.result?.name || line1,
    city,
    state,
    postal_code,
    country: countryShort,
  };
};

const SkypointSignup = () => {
  const [searchParams] = useSearchParams();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [customFormErrors, setCustomFormErrors] = useState({
    selectedTypes: null,
    otherValue: null,
  });

  // Address autocomplete state
  const [streetQuery, setStreetQuery] = useState("");
  const [addressOptions, setAddressOptions] = useState([]);
  // placeIdMap maps formatted_address label → place_id
  const placeIdMap = useRef({});
  const debounceTimer = useRef(null);

  const handleSelectChange = (value) => {
    if (!value) return;

    if (value === "Others") {
      setShowOtherInput(true);
    } else {
      setShowOtherInput(false);
      setOtherValue("");
      if (!selectedTypes.includes(value)) {
        setSelectedTypes([...selectedTypes, value]);
      }
    }
  };

  const handleOtherInputKeyDown = (event) => {
    if (event.key === "Enter" && otherValue.trim() !== "") {
      event.preventDefault();
      const newValue = otherValue.trim();
      if (!selectedTypes.includes(newValue)) {
        setSelectedTypes([
          ...selectedTypes.filter((v) => v !== "Other"),
          newValue,
        ]);
      }
      setOtherValue("");
      setCustomFormErrors((prev) => ({ ...prev, otherValue: null }));
    }
  };

  const form = useForm({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      companyName: "",
      phone: "",
      street: "",
      city: "",
      state: "",
      zipCode: "",
    },

    validate: {
      firstName: (value) => {
        if (!value) return "First Name Required";
        return /[a-zA-Z].*/.test(value)
          ? null
          : "Admin must contain at least one alphabet and may have numbers";
      },
      lastName: (value) => {
        if (!value) return "Last Name Required";
        return /[a-zA-Z].*/.test(value)
          ? null
          : "Admin must contain at least one alphabet and may have numbers";
      },
      email: (value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return !value
          ? "Email Required"
          : emailRegex.test(value)
          ? null
          : "Please enter a valid email address";
      },
      password: (value) => {
        if (!value) return "Password Required";
        const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).+$/;
        const isLengthValid = value.length >= 8;
        return passwordRegex.test(value) && isLengthValid
          ? null
          : "Password must contain at least one letter, one number, and be at least 8 characters long";
      },
      companyName: (value) => (!value ? "Business Name Required" : null),
      phone: (value) => (!value ? "Phone Number Required" : null),
      street: (value) => (!value ? "Street Address Required" : null),
      city: (value) => (!value ? "City Required" : null),
      state: (value) => (!value ? "State Required" : null),
      zipCode: (value) => (!value ? "Zip Code Required" : null),
    },
  });

  // Debounced search: fires 1000ms after the user stops typing
  const handleStreetChange = (value) => {
    setStreetQuery(value);
    form.setFieldValue("street", value);

    clearTimeout(debounceTimer.current);
    if (!value.trim()) {
      setAddressOptions([]);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      const results = await SignupsearchAddress(value);
      if (!Array.isArray(results)) return;
      placeIdMap.current = {};
      const labels = results.map((r) => {
        placeIdMap.current[r.formatted_address] = r.place_id;
        return r.formatted_address;
      });
      setAddressOptions(labels);
    }, 1000);
  };

  // When the user selects a suggestion, fetch full details and fill sub-fields
  const handleAddressSelect = async (label) => {
    setStreetQuery(label);
    const place_id = placeIdMap.current[label];
    if (!place_id) return;

    const data = await getSignupAddressByPlaceId(place_id);
    if (!data) return;

    const { line1, city, state, postal_code } = extractAddressFields(data);
    form.setFieldValue("street", line1);
    form.setFieldValue("city", city);
    form.setFieldValue("state", state);
    form.setFieldValue("zipCode", postal_code);
    setStreetQuery(line1);
    setAddressOptions([]);
  };

  const handleSubmit = async (values) => {
    try {
      setBtnLoading(true);
      let errors = {};

      if (selectedTypes.length === 0) {
        errors.selectedTypes = "Please select at least one contractor role";
      }

      if (showOtherInput && !otherValue.trim() && selectedTypes.length === 0) {
        errors.otherValue = "Please enter a contractor role or press Enter";
      }

      if (Object.keys(errors).length > 0) {
        setCustomFormErrors(errors);
        return;
      }

      setCustomFormErrors({ selectedTypes: null, otherValue: null });

      const address = [values.street, values.city, values.state, values.zipCode]
        .filter(Boolean)
        .join(", ");

      const payload = {
        ...values,
        address,
        name: `${form?.values?.firstName} ${form?.values?.lastName}`.trim(),
        selectedTypes,
        type: "contractor",
        platform: "Skypoint",
      };

      const isSignUp = await skypointSignUp(payload);
      if (isSignUp && isSignUp?.status === 200) {
        navigate("/skypoint-check-email");
      }
    } catch (error) {
    } finally {
      setBtnLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("email")) {
      form.setFieldValue("email", searchParams.get("email"));
    }
  }, [searchParams.get("email")]);

  return (
    <Grid className={classes.container}>
      <Grid.Col span={12} lg={6} className={classes.leftCol}>
        <Flex direction="column" align="center" className={classes.leftContent}>
          <Box className={classes.LogoBar} pr={14} pb={20} h={76} w="100%">
            <Image
              src="/assets/images/skypoint-logo2.png"
              height={44}
              width={144}
            />
          </Box>
          <Flex
            direction="column"
            justify="center"
            gap={32}
            className={classes.formAndHdngWrapper}
          >
            <Flex direction="column" gap={12}>
              <Text fz={36} fw={600} ff="Inter" color="#171717">
                Sign Up
              </Text>
              <Text fz={16} fw={400} ff="Inter" color="#737373">
                Welcome back! Please enter your details.
              </Text>
            </Flex>
            <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
              <Grid gutter={24}>
                <Grid.Col span={12} xs={6}>
                  <TextInput
                    id="signup-firstName"
                    className={classes.inputField}
                    label="First name"
                    placeholder="Enter your First Name"
                    {...form.getInputProps("firstName")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={6}>
                  <TextInput
                    id="signup-lastName"
                    className={classes.inputField}
                    label="Last name"
                    placeholder="Enter your Last Name"
                    {...form.getInputProps("lastName")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={6}>
                  <TextInput
                    id="signup-email"
                    className={classes.inputField}
                    label="Email"
                    placeholder="Enter your Email"
                    {...form.getInputProps("email")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={6}>
                  <PasswordInput
                    className={classes.inputField}
                    id="login-password"
                    label="Password"
                    placeholder="*****"
                    {...form.getInputProps("password")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={6}>
                  <TextInput
                    className={classes.inputField}
                    label="Business Name"
                    placeholder="Enter your Business Name"
                    {...form.getInputProps("companyName")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={6}>
                  <Select
                    label="Contractor Role"
                    placeholder="Select a role"
                    data={contractorRoles}
                    clearable
                    value={null}
                    onChange={(value) => handleSelectChange(value)}
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    className={classes.inputField}
                    error={customFormErrors.selectedTypes}
                    size="lg"
                  />
                </Grid.Col>
                {showOtherInput && (
                  <Grid.Col span={12}>
                    <TextInput
                      placeholder="Enter type and press Enter"
                      className={classes.inputField}
                      value={otherValue}
                      label="Other"
                      onChange={(e) => setOtherValue(e.target.value)}
                      onKeyDown={handleOtherInputKeyDown}
                      error={customFormErrors.otherValue}
                      size="lg"
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={12}>
                  <Text fw={500} mb={6} fz={14} ff="Inter" color="#404040">
                    Selected Roles
                  </Text>
                  <Flex
                    gap={8}
                    wrap="wrap"
                    className={classes.selectedTypesWrapper}
                  >
                    {selectedTypes?.map((type, index) => (
                      <Flex
                        gap={4}
                        align="center"
                        justify="center"
                        key={index}
                        className={classes.selectedTypeTag}
                      >
                        <Text
                          fz={14}
                          fw={500}
                          ff="Inter"
                          color="#344054"
                          align="center"
                        >
                          {type}
                        </Text>
                        <CloseButton
                          icon={<IoClose color="#667085" size={18} />}
                          onClick={() =>
                            setSelectedTypes(
                              selectedTypes.filter((t) => t !== type)
                            )
                          }
                        />
                      </Flex>
                    ))}
                  </Flex>
                </Grid.Col>
                {/* Street Address — autocomplete via backend proxy */}
                <Grid.Col span={12}>
                  <Autocomplete
                    id="signup-street"
                    className={classes.inputField}
                    label="Street Address"
                    placeholder="Start typing to search your address…"
                    value={streetQuery}
                    onChange={handleStreetChange}
                    onItemSubmit={(item) => handleAddressSelect(item.value)}
                    data={addressOptions}
                    error={form.errors.street}
                    size="lg"
                    filter={() => true}
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={4}>
                  <TextInput
                    id="signup-city"
                    className={classes.inputField}
                    label="City"
                    placeholder="City"
                    {...form.getInputProps("city")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={4}>
                  <TextInput
                    id="signup-state"
                    className={classes.inputField}
                    label="State"
                    placeholder="State"
                    {...form.getInputProps("state")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12} xs={4}>
                  <TextInput
                    id="signup-zipCode"
                    className={classes.inputField}
                    label="Zip Code"
                    placeholder="Zip Code"
                    {...form.getInputProps("zipCode")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    id="signup-phone"
                    className={classes.inputField}
                    label="Phone number"
                    placeholder="Enter your Phone Number"
                    type="number"
                    {...form.getInputProps("phone")}
                    size="lg"
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Button
                    id="signup-button"
                    className={classes.signupBtn}
                    type="submit"
                    loading={btnLoading}
                  >
                    Sign up
                  </Button>
                </Grid.Col>
              </Grid>
            </form>
            <Flex
              justify="center"
              align="center"
              gap={4}
              className={classes.loginLinkText}
            >
              <Text fz={14} fw={400} ff="Inter" color="#737373">
                Already have an account?
              </Text>
              <Link className="loginLink" to={"/skypoint-login"}>
                Login
              </Link>
            </Flex>
          </Flex>
        </Flex>
      </Grid.Col>
      <Grid.Col span={12} lg={6} className={classes.rightCol}>
        <Flex
          className={classes.rightContent}
          align="center"
          justify="flex-end"
        >
          <Box className={classes.linePattern1Parent}>
            <LinePatternSvg />
          </Box>
          <img
            src="/assets/images/skypoint-invoice.png"
            alt=""
            className={classes.invoiceImg}
          />
          <Box className={classes.linePattern2Parent}>
            <LinePatternSvg />
          </Box>
        </Flex>
      </Grid.Col>
    </Grid>
  );
};

export default SkypointSignup;
